import { Text, Flex, Heading, Button } from '@everlywell/ui-kit';
import SectionContainer from 'components/PageSections/components/SectionContainer';
import { Link } from 'gatsby';
import React from 'react';
import analytics from 'utils/analytics';
import { ANALYTICS } from 'utils/constants/analytics';
import { ContentfulRichText } from 'utils/contentful/renderRichText';
import useMdpModals from 'utils/hooks/useMdpModals';
import { ImageType } from 'utils/types';

import LearnMoreForm from './LearnMoreForm';
import WeightLossSlider from './WeightLossSlider';

export type WeightLossSliderProps = {
  weightLossSlider?: {
    body: ContentfulRichText;
    eyebrow: string;
    desktopImage?: ImageType | null;
    metadata: {
      order: number;
      context: {
        calculation: {
          percentage: number;
        };
      };
      theme: {
        backgroundColor: string;
        textColor: string;
        primaryColor: string;
      };
    };
  }[];
};

export type LearnMoreFormProps = {
  learnMoreForm?: {
    eyebrow: string;
    metadata: {
      title: string;
      order: number;
      theme: {
        backgroundColor: string;
        textColor: string;
        primaryColor: string;
      };
    };
  }[];
};
export interface InteractiveBannerProps
  extends WeightLossSliderProps,
    LearnMoreFormProps {
  eyebrow?: string;
  title?: string;
  theme?: {
    backgroundColor: string;
    textColor: string;
    primaryColor: string;
  };
  cta?: {
    label: string;
    url: string;
  };
  desktopImage?: ImageType | null;
  order?: number;
}

const InteractiveBanner: React.FC<InteractiveBannerProps> = ({
  eyebrow,
  title,
  theme,
  order,
  cta,
  desktopImage,
  weightLossSlider,
  learnMoreForm,
}) => {
  const { handleMdpCtaClick } = useMdpModals();

  const handleCtaClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    handleMdpCtaClick({
      event,
      ctaUrl: cta?.url || '',
    });
    trackCtaClick();
  };

  const trackCtaClick = () => {
    analytics.track({
      event: ANALYTICS.EVENTS.CLICKED_LINK,
      data: {
        sectionHeader: title || 'No title provided',
        contentfulSection: 'InteractiveBanner',
        ctaText: cta?.label || 'No CTA label',
      },
    });
  };

  return (
    <SectionContainer size={['sm', 'sm', 'lg', 'xl']}>
      <Flex
        background={theme?.backgroundColor}
        padding={4}
        borderRadius={'lg'}
        display={[null, 'flex']}
        gap={4}
      >
        <Flex
          order={order}
          flex={1}
          flexFlow="column"
          justifyContent="space-evenly"
          alignItems="flex-start"
          marginBottom={[8, 0]}
          backgroundImage={desktopImage?.gatsbyImageData?.images?.fallback?.src}
          backgroundRepeat="no-repeat"
          backgroundPosition={['center 150%', 'center bottom']}
          backgroundSize="cover"
          borderRadius={'lg'}
          padding={[4, 6]}
        >
          {eyebrow ? (
            <Text size="lg" color="viridian.base" marginBottom={[2, 0]}>
              {eyebrow}
            </Text>
          ) : null}
          {title ? (
            <Heading
              as="h2"
              size="2xl"
              maxWidth={[null, '460px']}
              marginBottom={[8, 20]}
              color={theme?.textColor}
            >
              {title}
            </Heading>
          ) : null}
          {cta ? (
            <Button
              as={Link}
              size={['wide', 'sm']}
              to={cta.url}
              onClick={handleCtaClick}
            >
              {cta.label}
            </Button>
          ) : null}
        </Flex>
        {weightLossSlider && weightLossSlider.length > 0 ? (
          <WeightLossSlider weightLossSlider={weightLossSlider} />
        ) : null}
        {learnMoreForm && learnMoreForm.length > 0 ? (
          <LearnMoreForm learnMoreForm={learnMoreForm} />
        ) : null}
      </Flex>
    </SectionContainer>
  );
};

export default InteractiveBanner;
